import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { useSession } from "../../firebase/UserProvider";
import {
  getTaskByNumber, getComments, addComment, updateTask,
  subscribeToTask, subscribeToLabels, listTaskUsers, listTasks,
  completeRecurringTask, completeTaskForever
} from "../../firebase/tasks";
import { TaskCircle } from "../../components/tms/TaskCircle";
import { TaskRightPanel } from "../../components/tms/TaskRightPanel";
import { CommentThread } from "../../components/tms/CommentThread";
import { TaskTitleInput } from "../../components/tms/TaskTitleInput";
import { LabelTag } from "../../components/tms/LabelTag";
import { PriorityPicker } from "../../components/tms/PriorityPicker";
import { DatePicker } from "../../components/tms/DatePicker";
import { UserSelectDropdown } from "../../components/tms/UserSelectDropdown";
import { RecurrencePicker } from "../../components/tms/RecurrencePicker";
import { UndoToast } from "../../components/tms/UndoToast";
import { FaRegUser } from "react-icons/fa";
import { FiTag, FiSend, FiX } from "react-icons/fi";
/**
 * TaskDetail Page Component
 * 
 * Renders the two-panel detail view for a task.
 * Left Panel: Clickable TaskCircle status toggler, Title, Description, and CommentThread.
 * Right Panel: TaskRightPanel displaying task parameters.
 * Header Bar: Inbox breadcrumb, Previous/Next task navigation, Actions menu, Close button.
 */
const parseInput = (text, sysLabels) => {
  const words = text.trim().split(/\s+/);
  const labels = [];
  let i = 0;
  while (i < words.length && words[i].startsWith("@")) {
    const name = words[i].slice(1);
    const match = sysLabels.find(l => l.name.toLowerCase() === name.toLowerCase());
    if (!match) break;
    if (!labels.includes(match.name)) labels.push(match.name);
    i++;
  }
  return { title: words.slice(i).join(" "), labels };
};

export default function TaskDetail() {
  const { taskNumber } = useParams();
  const { user } = useSession();
  const history = useHistory();

  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [labels, setLabels] = useState([]);
  const [allNums, setAllNums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [hoverWarn, setHoverWarn] = useState(null);
  const menuRef = useRef(null);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState("");
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [descInput, setDescInput] = useState("");
  // All label names gathered from every task document (for cross-task @-mention autocomplete)
  const [allTaskLabels, setAllTaskLabels] = useState([]);
  // Undo toast for recurring task completion
  const [undoToast, setUndoToast] = useState({ visible: false, message: "", taskId: null, previousDueDate: null });

  // Merge:
  //  1. system labels from task-labels collection (subscribeToLabels)
  //  2. labels on the current task (for labels added before task-labels docs existed)
  //  3. labels from ALL other tasks (cross-task availability)
  // This ensures any label ever added in any view appears in the @-mention autocomplete.
  const effectiveLabels = Array.from(
    new Set([
      ...labels.map(l => l.name.toLowerCase()),
      ...((task?.labels || []).map(l => l.toLowerCase())),
      ...allTaskLabels
    ])
  ).map(name => {
    const sys = labels.find(l => l.name.toLowerCase() === name);
    return sys || { id: name, name, color: "#6b7280" };
  });

  const updateTaskField = async (field, val, logType = null, logText = null, oldV = null, newV = null) => {
    try {
      if (logType) {
        const authorName = user?.displayName || user?.email || "System";
        await addComment(task.id, {
          authorUid: user?.uid || "system",
          authorName: authorName,
          isSystem: true,
          type: logType,
          text: logText ? `${logText} by ${authorName}` : logText,
          oldValue: oldV,
          newValue: newV,
        });
      }
      await updateTask(task.id, { [field]: val, updatedByUid: user?.uid || "system" });
      await fetchComments(task.id);
    } catch (err) {
      console.error(err);
    }
  };

  const getUserName = (uid) => {
    if (!uid) return "Unassigned";
    const matched = users.find(u => u.uid === uid);
    return matched ? matched.name : "Unknown User";
  };

  const handleSaveTitle = async (inputTextVal) => {
    const parsed = parseInput(inputTextVal, labels);
    try {
      await updateTask(task.id, {
        title: parsed.title.trim(),
        updatedByUid: user?.uid || "system"
      });
      setIsEditingTitle(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleLabelsChange = async (newLabels) => {
    try {
      await updateTask(task.id, { labels: newLabels, updatedByUid: user?.uid || "system" });
      await fetchComments(task.id);
    } catch (err) {
      console.error(err);
    }
  };

  // Called when user selects a label via @-mention in the title field.
  // Always ensures the label exists in task-labels collection (idempotent createLabel),
  // then adds it to task.labels if not already present.
  const handleAddLabel = async (labelName) => {
    const normalizedName = labelName.toLowerCase().trim();
    const current = task.labels || [];
    const alreadyOnTask = current.some(l => l.toLowerCase() === normalizedName);
    if (alreadyOnTask) return;  // already on this task, no Firestore update needed
    await handleLabelsChange([...current, normalizedName]);
  };

  const handleRemoveLabel = async (labelName) => {
    const updated = (task.labels || []).filter(l => l !== labelName);
    await handleLabelsChange(updated);
  };

  useEffect(() => {
    listTaskUsers().then(setUsers).catch(console.error);
    const unsubLabels = subscribeToLabels(setLabels);
    listTasks().then((list) => {
      setAllNums(list.map((t) => Number(t.taskNumber)).filter((n) => !isNaN(n)).sort((a, b) => a - b));
      // Collect all unique label names across every task for cross-task autocomplete
      const allNames = Array.from(
        new Set(list.flatMap(t => (t.labels || []).map(l => l.toLowerCase())))
      );
      setAllTaskLabels(allNames);
    }).catch(console.error);
    return () => unsubLabels && unsubLabels();
  }, []);

  const fetchComments = async (taskId) => {
    try {
      const list = await getComments(taskId);
      setComments(list);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    let unsub;
    const load = async () => {
      if (!taskNumber) return;
      setLoading(true);
      try {
        const data = await getTaskByNumber(Number(taskNumber));
        if (data) {
          setTask(data);
          await fetchComments(data.id);
          unsub = subscribeToTask(data.id, (u) => u && setTask(u));
        } else {
          setTask(null);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => unsub && unsub();
  }, [taskNumber]);

  useEffect(() => {
    if (!showMenu) return;
    const click = (e) => menuRef.current && !menuRef.current.contains(e.target) && setShowMenu(false);
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, [showMenu]);

  const handleToggleStatus = async () => {
    if (!task) return;

    if (task.status !== "Closed" && task.recurrence?.isRecurring) {
      try {
        const authorName = user?.displayName || user?.email || "System";
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        const result = await completeRecurringTask(task.id, todayStr, user?.uid || "system", authorName);
        if (result.advanced) {
          setUndoToast({
            visible: true,
            message: `"${task.title}" completed — next due ${result.nextDueDate}`,
            taskId: task.id,
            previousDueDate: result.previousDueDate,
          });
        }
        await fetchComments(task.id);
      } catch (err) {
        console.error(err);
      }
      return;
    }

    const newStatus = task.status === "Closed" ? "Open" : "Closed";
    try {
      const authorName = user?.displayName || user?.email || "System";
      await addComment(task.id, {
        authorUid: user?.uid || "system",
        authorName: authorName,
        isSystem: true,
        type: "state_change",
        text: `Status changed from "${task.status}" to "${newStatus}" by ${authorName}`,
        oldValue: task.status,
        newValue: newStatus,
      });
      await updateTask(task.id, { status: newStatus, updatedByUid: user?.uid || "system" });
      await fetchComments(task.id);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCompleteForever = async () => {
    if (!task) return;
    try {
      const authorName = user?.displayName || user?.email || "System";
      await completeTaskForever(task.id, user?.uid || "system", authorName);
      await fetchComments(task.id);
      setShowMenu(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUndoRecurrence = async () => {
    if (!undoToast.taskId || !undoToast.previousDueDate) return;
    try {
      await updateTask(undoToast.taskId, {
        dueDate: undoToast.previousDueDate,
        status: "Open",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddComment = async (text) => {
    if (!task) return;
    try {
      await addComment(task.id, {
        text,
        authorUid: user?.uid || "system",
        authorName: user?.displayName || user?.email || "Team Member",
        type: "user",
      });
      await fetchComments(task.id);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 flex justify-center items-center bg-white font-semibold text-sm text-gray-400 select-none">
        Synchronizing task details...
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen py-12 flex justify-center items-center bg-white">
        <div className="rounded-2xl border border-gray-200 shadow-sm py-12 px-8 text-center max-w-md w-full bg-white">
          <h2 className="text-lg font-bold text-gray-800">Task Not Found</h2>
          <p className="mt-2 text-sm text-gray-500 font-medium">The requested task #{taskNumber} does not exist.</p>
          <Link to="/tasks" className="mt-6 inline-flex justify-center font-bold py-2 px-5 bg-red-500 hover:bg-red-650 text-white rounded-md text-xs select-none shadow-sm transition-all duration-150">
            Return to Tasks List
          </Link>
        </div>
      </div>
    );
  }

  const curIdx = allNums.indexOf(Number(taskNumber));
  const prevNum = curIdx > 0 ? allNums[curIdx - 1] : null;
  const nextNum = curIdx !== -1 && curIdx < allNums.length - 1 ? allNums[curIdx + 1] : null;

  return (
    <div className="min-h-screen bg-white py-6 pb-36 px-4 md:px-8 select-text">
      {/* Header Bar */}
      <div className="max-w-5xl mx-auto flex items-center justify-between pb-3 border-b border-gray-150 mb-6 select-none">
        <Link to="/tasks" className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 font-bold text-sm transition-colors">
          📥 Inbox
        </Link>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={!prevNum}
            onClick={() => prevNum && history.push(`/tasks/${prevNum}`)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-md disabled:opacity-30 text-gray-500 hover:text-gray-800 font-bold transition-colors focus:outline-none border-none"
            title="Previous task"
          >
            ∧
          </button>
          <button
            type="button"
            disabled={!nextNum}
            onClick={() => nextNum && history.push(`/tasks/${nextNum}`)}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-md disabled:opacity-30 text-gray-500 hover:text-gray-800 font-bold transition-colors focus:outline-none border-none"
            title="Next task"
          >
            ∨
          </button>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setShowMenu(!showMenu)}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-150 rounded-md text-gray-500 hover:text-gray-800 font-bold transition-colors focus:outline-none text-base border-none"
              title="More actions"
            >
              ···
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 text-xs font-semibold text-gray-700">
                <button
                  type="button"
                  onClick={() => setShowMenu(false)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-none"
                >
                  📄 Duplicate task
                </button>
                {task?.recurrence?.isRecurring && (
                  <button
                    type="button"
                    onClick={handleCompleteForever}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-none text-red-600"
                  >
                    ⏹ Complete forever
                  </button>
                )}
              </div>
            )}
          </div>
          <Link
            to="/tasks"
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-md text-gray-500 hover:text-gray-800 text-xl transition-colors focus:outline-none font-bold"
            title="Close"
          >
            &times;
          </Link>
        </div>
      </div>

      {/* Main Layout Grid */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 items-start">

        {/* LEFT COLUMN */}
        <div className="space-y-6">

          {/* Title row */}
          <div className="flex items-start gap-3">
            <div className="mt-1.5 flex-shrink-0">
              <TaskCircle status={task.status} priority={task.priority} onClick={handleToggleStatus} isRecurring={!!task.recurrence?.isRecurring} />
            </div>
            <div className="flex-1 min-w-0">
              {isEditingTitle ? (
                <div className="space-y-2">
                  <div className="border border-gray-250 hover:border-gray-350 focus-within:border-gray-400 p-2.5 rounded-lg transition-all bg-white">
                    <TaskTitleInput
                      value={titleInput}
                      onChange={setTitleInput}
                      onAddLabel={handleAddLabel}
                      allLabels={effectiveLabels}
                      autoFocus={true}
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <div className="relative group/cancel">
                      <button
                        type="button"
                        onClick={() => setIsEditingTitle(false)}
                        className="w-8 h-8 flex items-center justify-center text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors duration-150 border-none outline-none focus:outline-none cursor-pointer"
                        aria-label="Cancel"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/cancel:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
                        Cancel
                      </span>
                    </div>
                    
                    <div className="relative group/save">
                      <button
                        type="button"
                        onClick={() => handleSaveTitle(titleInput)}
                        disabled={!titleInput.trim()}
                        className="w-8 h-8 flex items-center justify-center text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed border-none outline-none focus:outline-none cursor-pointer"
                        aria-label="Save"
                      >
                        <FiSend className="w-4 h-4" />
                      </button>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/save:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
                        Save
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <h2
                  onClick={() => {
                    // Initialise with the clean title only — labels are shown separately
                    // in the right panel and must NOT be prepended to the input.
                    setTitleInput(task.title);
                    setIsEditingTitle(true);
                  }}
                  className="text-2xl font-bold text-gray-800 leading-tight break-words cursor-pointer hover:bg-gray-50 p-1.5 rounded transition-colors"
                  title="Click to edit title"
                >
                  <span className="text-gray-400 mr-2 font-medium select-none">#{task.taskNumber}</span>
                  {task.title}
                </h2>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="pl-8 space-y-1.5">
            {isEditingDesc ? (
              <div className="space-y-2">
                <textarea
                  value={descInput}
                  onChange={(e) => setDescInput(e.target.value)}
                  placeholder="Description"
                  rows={4}
                  className="w-full p-3 border border-gray-250 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-gray-400 transition-all font-medium resize-none min-h-[80px]"
                />
                <div className="flex gap-2 justify-end">
                  <div className="relative group/cancel">
                    <button
                      type="button"
                      onClick={() => setIsEditingDesc(false)}
                      className="w-8 h-8 flex items-center justify-center text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors duration-150 border-none outline-none focus:outline-none cursor-pointer"
                      aria-label="Cancel"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/cancel:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
                      Cancel
                    </span>
                  </div>
                  
                  <div className="relative group/save">
                    <button
                      type="button"
                      onClick={async () => {
                        await updateTask(task.id, { description: descInput.trim(), updatedByUid: user?.uid || "system" });
                        setIsEditingDesc(false);
                      }}
                      className="w-8 h-8 flex items-center justify-center text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm transition-colors duration-150 border-none outline-none focus:outline-none cursor-pointer"
                      aria-label="Save"
                    >
                      <FiSend className="w-4 h-4" />
                    </button>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/save:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
                      Save
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p
                onClick={() => {
                  setDescInput(task.description || "");
                  setIsEditingDesc(true);
                }}
                className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words font-medium cursor-pointer hover:bg-gray-50 p-1.5 rounded transition-colors min-h-[24px]"
                title="Click to edit description"
              >
                {task.description || "No description provided."}
              </p>
            )}
          </div>

          {/* Mobile-only: Task Fields */}
          <div className="block lg:hidden pl-0 border-t border-gray-100">
            <div className="space-y-0 text-left">

              {/* DUE */}
              <div
                className="flex items-center justify-between py-3 border-b border-gray-100 text-sm relative"
                onMouseEnter={() => setHoverWarn("dueDate")}
                onMouseLeave={() => setHoverWarn(null)}
              >
                <div className="flex items-center gap-2 text-gray-500 font-medium">
                  <span className="text-gray-400">📅</span>
                  <span>DUE</span>
                </div>
                <DatePicker value={task.dueDate} onChange={(d) => updateTaskField("dueDate", d || null, "date_change", `Due date changed from "${task.dueDate || "none"}" to "${d || "none"}"`, task.dueDate || null, d || null)} dropdownAlign="right" />
                {hoverWarn === "dueDate" && <div className="absolute top-1 right-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">Changes will be logged</div>}
              </div>

              {/* REPEAT */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100 text-sm">
                <div className="flex items-center gap-2 text-gray-500 font-medium">
                  <span className="text-gray-400">🔄</span>
                  <span>REPEAT</span>
                </div>
                <RecurrencePicker
                  value={task.recurrence || null}
                  onChange={(rec) => updateTaskField("recurrence", rec, null, null, null, null)}
                  dropdownAlign="right"
                />
              </div>

              {/* ASSIGNEE */}
              <div
                className="flex items-center justify-between py-3 border-b border-gray-100 text-sm relative"
                onMouseEnter={() => setHoverWarn("assignee")}
                onMouseLeave={() => setHoverWarn(null)}
              >
                <div className="flex items-center gap-2 text-gray-500 font-medium">
                  <span className="text-gray-400"><FaRegUser className="w-3.5 h-3.5" /></span>
                  <span>ASSIGNEE</span>
                </div>
                <UserSelectDropdown
                  users={users}
                  value={task.assigneeUid || ""}
                  onChange={(finalAssignee) => updateTaskField("assigneeUid", finalAssignee, "assignment_change", `Assignee changed from "${getUserName(task.assigneeUid)}" to "${getUserName(finalAssignee)}"`, getUserName(task.assigneeUid), getUserName(finalAssignee))}
                  buttonClassName="bg-transparent hover:bg-gray-50 px-2 py-1 rounded cursor-pointer font-bold text-gray-700 focus:outline-none transition-colors border border-transparent appearance-none min-w-[120px] text-right flex justify-end gap-2 items-center"
                  dropdownAlign="right"
                />
                {hoverWarn === "assignee" && <div className="absolute top-1 right-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">Changes will be logged</div>}
              </div>

              {/* PRIORITY */}
              <div
                className="flex items-center justify-between py-3 border-b border-gray-100 text-sm relative"
                onMouseEnter={() => setHoverWarn("priority")}
                onMouseLeave={() => setHoverWarn(null)}
              >
                <div className="flex items-center gap-2 text-gray-500 font-medium">
                  <span className="text-gray-400">🏴</span>
                  <span>PRIORITY</span>
                </div>
                <PriorityPicker value={task.priority} onChange={(p) => updateTaskField("priority", p, "state_change", `Priority changed from "${task.priority}" to "${p}"`, task.priority, p)} dropdownAlign="right" />
                {hoverWarn === "priority" && <div className="absolute top-1 right-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">Changes will be logged</div>}
              </div>

              {/* STATUS */}
              <div
                className="flex items-center justify-between py-3 border-b border-gray-100 text-sm relative"
                onMouseEnter={() => setHoverWarn("status")}
                onMouseLeave={() => setHoverWarn(null)}
              >
                <div className="flex items-center gap-2 text-gray-500 font-medium">
                  <span className="text-gray-400">🔘</span>
                  <span>STATUS</span>
                </div>
                <select
                  value={task.status || "Open"}
                  onChange={(e) => updateTaskField("status", e.target.value, "state_change", `Status changed from "${task.status}" to "${e.target.value}"`, task.status, e.target.value)}
                  className="bg-transparent hover:bg-gray-50 px-2 py-1 rounded cursor-pointer font-bold text-gray-700 focus:outline-none transition-colors border border-transparent appearance-none"
                  style={{ textAlignLast: 'right' }}
                >
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Closed">Closed</option>
                </select>
                {hoverWarn === "status" && <div className="absolute top-1 right-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">Changes will be logged</div>}
              </div>

              {/* LABELS */}
              <div
                className="py-3 border-b border-gray-100 text-sm"
              >
                <div className="flex items-center gap-2 text-gray-500 font-medium mb-2">
                  <span className="text-gray-400"><FiTag className="w-3.5 h-3.5" /></span>
                  <span>LABELS</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(task.labels || []).length > 0 ? (
                    (task.labels || []).map((lName) => {
                      const labelColor = labels.find(l => l.name.toLowerCase() === lName.toLowerCase())?.color;
                      return (
                        <LabelTag
                          key={lName}
                          name={lName}
                          color={labelColor}
                          onRemove={handleRemoveLabel}
                        />
                      );
                    })
                  ) : (
                    <span className="text-gray-400 text-xs italic font-medium select-none">Type @ in title to add labels</span>
                  )}
                </div>
              </div>


            </div>
          </div>

          {/* Comments */}
          <div className="pl-8">
            <hr className="border-gray-100 mb-4" />
            <CommentThread comments={comments} user={user} onAddComment={handleAddComment} />
          </div>

          {/* Mobile-only: Task Metadata footer */}
          <div className="block lg:hidden pl-0 border-t border-gray-100 pt-4 space-y-2 text-[10px] text-gray-400 font-semibold select-none">
            <div>
              <span>Created by: </span>
              <span className="text-gray-600 font-bold">{getUserName(task.createdByUid)}</span>
            </div>
            <div>
              <span>Created at: </span>
              <span className="text-gray-500">{task.createdAt ? new Date(task.createdAt.seconds ? task.createdAt.seconds * 1000 : task.createdAt).toLocaleString() : "N/A"}</span>
            </div>
            <div>
              <span>Last updated: </span>
              <span className="text-gray-500">{task.updatedAt ? new Date(task.updatedAt.seconds ? task.updatedAt.seconds * 1000 : task.updatedAt).toLocaleString() : "N/A"}</span>
            </div>
            <div>
              <span>Last updated by: </span>
              <span className="text-gray-600 font-bold">{getUserName(task.updatedByUid)}</span>
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN — desktop only */}
        <div className="hidden lg:block select-none">
          <TaskRightPanel
            task={task}
            users={users}
            labels={labels}
            user={user}
            onFieldSaved={async () => fetchComments(task.id)}
          />
        </div>

      </div>

      <UndoToast
        message={undoToast.message}
        visible={undoToast.visible}
        onUndo={handleUndoRecurrence}
        onDismiss={() => setUndoToast({ visible: false, message: "", taskId: null, previousDueDate: null })}
      />
    </div>
  );
}
